import { rd } from '@passionware/monads';
import { createMatcher } from '@passionware/query-toolkit';
import { useWatch } from 'react-hook-form';
import { FormField } from 'v5/design-system/Form';
import {
  MultiSelect,
  MultiSelectContent,
  MultiSelectGroup,
  MultiSelectItem,
  MultiSelectQueryRenderer,
  MultiSelectTrigger
} from 'v5/design-system/MultiSelect';
import { useFieldContext } from '../FieldContext';
import { EmptyField } from './EmptyField';
import { FieldControlBase } from './FieldControlBase';
import type { FieldSpecOf } from 'v5/core/entity-schema/entitySchema.types';
import type {
  FieldFrameSpec,
  FieldPartProps,
  PlaceholderSpec
} from './fieldComponent.types';

/** Non-creatable multi-select over `string[]` option values. */
export function MultiSelectField({
  spec,
  path,
  hideLabel
}: FieldPartProps<
  FieldFrameSpec &
    PlaceholderSpec &
    Pick<FieldSpecOf<'multi-select'>, 'options'>
>) {
  const ctx = useFieldContext();
  const size = ctx.size === 'lg' ? 'md' : ctx.size;
  const readOnly = ctx.mode === 'display';
  const values =
    useWatch<Record<string, string[] | undefined>>({ name: path }) ?? [];

  if (readOnly && values.length === 0) {
    return <EmptyField spec={spec} path={path} hideLabel={hideLabel} />;
  }

  return (
    <FormField
      name={path}
      render={({ field: { onChange, ref, value } }) => {
        const selected: string[] = Array.isArray(value) ? value : [];
        return (
          <FieldControlBase spec={spec} hideLabel={hideLabel}>
            <MultiSelect value={selected} onValueChange={onChange}>
              <MultiSelectTrigger
                size={size}
                variant={ctx.variant}
                placeholder={
                  spec.placeholder ?? `Select ${spec.label.toLowerCase()}`
                }
                readOnly={readOnly}
                ref={ref}
              >
                {values =>
                  rd.of(
                    values.map(
                      selectedValue =>
                        spec.options.find(o => o.value === selectedValue)
                          ?.label ?? selectedValue
                    )
                  )
                }
              </MultiSelectTrigger>
              <MultiSelectContent>
                <MultiSelectQueryRenderer>
                  {query =>
                    rd.of(
                      <MultiSelectGroup>
                        {spec.options
                          .filter(createMatcher(query, o => o.label))
                          .map(option => (
                            <MultiSelectItem
                              key={option.value}
                              value={option.value}
                            >
                              {option.label}
                            </MultiSelectItem>
                          ))}
                      </MultiSelectGroup>
                    )
                  }
                </MultiSelectQueryRenderer>
              </MultiSelectContent>
            </MultiSelect>
          </FieldControlBase>
        );
      }}
    />
  );
}
